/**
 * AssignmentDialog — multi-select escalation target picker.
 *
 * Used both as a stand-alone "Assign" action and as the body of the
 * "Escalate" flow. When `mode='escalate'` we send to the escalate
 * endpoint (which also flips the ticket status). Otherwise we PATCH
 * the assignment list via `/assign/`.
 *
 * Saving replaces the whole assignment list, so the dialog shows the
 * pending diff — who is being added and who is being dropped — before the
 * operator commits. The people search is the combobox's own field; a
 * second box next to it (one searching the server, one filtering locally)
 * only ever sent people looking in the wrong place.
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useEffect, useMemo, useState } from 'react';
import { ShieldAlert, UserMinus, UserPlus } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Label } from '@/components/ui/Label';
import { MultiCombobox } from '@/components/ui/MultiCombobox';
import {
  useAssignCrmTicket,
  useCrmAssignees,
  useEscalateCrmTicket,
  type CrmAssignment,
  type CrmTicket,
} from '@/api/bi/crm';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';

interface AssignmentDialogProps {
  ticket: CrmTicket;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'assign' | 'escalate';
}

export function AssignmentDialog({ ticket, open, onOpenChange, mode }: AssignmentDialogProps) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query.trim(), 250);
  const assignees = useCrmAssignees(debouncedQuery);
  const assign = useAssignCrmTicket(ticket.id);
  const escalate = useEscalateCrmTicket(ticket.id);

  const initialIds = useMemo(
    () => (ticket.assignments ?? []).map((a) => String(a.user?.id ?? '')).filter(Boolean),
    [ticket.assignments],
  );
  const initialPrimaryId = useMemo(
    () =>
      (ticket.assignments ?? []).find((a: CrmAssignment) => a.is_primary)?.user?.id ?? null,
    [ticket.assignments],
  );

  const [selectedIds, setSelectedIds] = useState<string[]>(initialIds);
  const [primaryId, setPrimaryId] = useState<string | null>(
    initialPrimaryId != null ? String(initialPrimaryId) : null,
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelectedIds(initialIds);
    setPrimaryId(initialPrimaryId != null ? String(initialPrimaryId) : null);
    setNote('');
    setError(null);
    setQuery('');
  }, [open, initialIds, initialPrimaryId]);

  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of ticket.assignments ?? []) {
      if (a.user) map.set(String(a.user.id), a.user.name || `User #${a.user.id}`);
    }
    for (const u of assignees.data?.results ?? []) {
      map.set(String(u.id), u.name || `User #${u.id}`);
    }
    return map;
  }, [assignees.data, ticket.assignments]);

  const options = useMemo(() => {
    const seen = new Map<string, { value: string; label: string; hint?: string }>();
    // Include current assignees so they remain visible even if not in the search results.
    for (const a of ticket.assignments ?? []) {
      if (a.user) {
        seen.set(String(a.user.id), {
          value: String(a.user.id),
          label: a.user.name || `User #${a.user.id}`,
          hint: a.user.role ?? undefined,
        });
      }
    }
    for (const u of assignees.data?.results ?? []) {
      seen.set(String(u.id), {
        value: String(u.id),
        label: u.name,
        hint: u.role ?? undefined,
      });
    }
    return Array.from(seen.values());
  }, [assignees.data, ticket.assignments]);

  const primaryOptions = useMemo(
    () => options.filter((o) => selectedIds.includes(o.value)),
    [options, selectedIds],
  );

  const added = useMemo(
    () => selectedIds.filter((id) => !initialIds.includes(id)),
    [selectedIds, initialIds],
  );
  const removed = useMemo(
    () => initialIds.filter((id) => !selectedIds.includes(id)),
    [selectedIds, initialIds],
  );
  const primaryChanged =
    (primaryId ?? '') !== (initialPrimaryId != null ? String(initialPrimaryId) : '');
  const hasChanges = added.length > 0 || removed.length > 0 || primaryChanged;

  const isSubmitting = assign.isPending || escalate.isPending;

  async function handleSubmit() {
    if (selectedIds.length === 0) {
      setError('Pick at least one person to own this ticket.');
      return;
    }
    if (mode === 'escalate' && !note.trim()) {
      setError('Say why this is being escalated — the note is what the manager reads first.');
      return;
    }
    setError(null);
    const userIds = selectedIds.map((s) => Number(s)).filter((n) => Number.isFinite(n));
    const primary = primaryId ? Number(primaryId) : null;
    try {
      if (mode === 'escalate') {
        await escalate.mutateAsync({
          user_ids: userIds,
          primary,
          escalation_note: note,
        });
        notify.success('Escalated and notified assignees.');
      } else {
        await assign.mutateAsync({ user_ids: userIds, primary, note });
        notify.success('Assignments updated.');
      }
      onOpenChange(false);
    } catch (err) {
      const message = apiErrorMessage(err, 'Could not save the assignments.');
      setError(message);
      notify.error(message);
    }
  }

  const title = mode === 'escalate' ? 'Escalate ticket' : 'Assign ticket';
  const TitleIcon = mode === 'escalate' ? ShieldAlert : UserPlus;
  const subtitle =
    mode === 'escalate'
      ? 'Pick one or more managers, admins or account staff to escalate this to. The ticket status moves to escalated.'
      : 'This replaces the assignment list. Mark one person as primary owner — the ticket becomes their main task.';

  return (
    <Dialog open={open} onOpenChange={(next) => (isSubmitting ? null : onOpenChange(next))}>
      <DialogContent className="max-w-xl">
        <div className="space-y-4">
          <div>
            <DialogTitle asChild>
              <h2 className="font-display text-base font-semibold inline-flex items-center gap-2">
                <TitleIcon className="h-4 w-4" />
                {title}
              </h2>
            </DialogTitle>
            <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-foreground">Assignees</Label>
            <MultiCombobox
              values={selectedIds}
              onChange={(next) => {
                setSelectedIds(next);
                if (primaryId && !next.includes(primaryId)) setPrimaryId(null);
                if (error) setError(null);
              }}
              onSearchChange={setQuery}
              options={options}
              placeholder="Search admins, managers, account, operation, support…"
              searchPlaceholder="Search by name or role…"
              allLabel="No assignees"
              countNoun="assignee"
              triggerClassName="w-full"
              width={420}
              loading={assignees.isLoading}
              emptyMessage={
                debouncedQuery ? 'Nobody matches that name.' : 'Type a name to search.'
              }
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-foreground">Primary owner (optional)</Label>
            <Combobox
              value={primaryId}
              onChange={setPrimaryId}
              options={primaryOptions}
              placeholder="No primary"
              clearLabel="No primary"
              emptyMessage="Add at least one assignee first."
              triggerClassName="w-full"
              width={420}
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor={`assignment-note-${ticket.id}`}
              className="text-xs font-medium text-foreground"
            >
              {mode === 'escalate' ? 'Escalation note' : 'Note for assignees'}
            </Label>
            <textarea
              id={`assignment-note-${ticket.id}`}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (error) setError(null);
              }}
              rows={3}
              className="w-full rounded-md border border-input bg-surface px-3 py-2 text-xs"
              placeholder={
                mode === 'escalate'
                  ? 'Why is this being escalated? Customer impact, repeat issue, etc.'
                  : 'Optional note attached to every assignee.'
              }
            />
          </div>

          {hasChanges ? (
            <div className="space-y-1.5 rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px]">
              {added.length > 0 ? (
                <p className="flex flex-wrap items-center gap-1.5">
                  <Badge tone="success" className="gap-1">
                    <UserPlus className="h-3 w-3" /> Adding
                  </Badge>
                  <span>{added.map((id) => labelById.get(id) ?? `#${id}`).join(', ')}</span>
                </p>
              ) : null}
              {removed.length > 0 ? (
                <p className="flex flex-wrap items-center gap-1.5">
                  <Badge tone="danger" className="gap-1">
                    <UserMinus className="h-3 w-3" /> Removing
                  </Badge>
                  <span>{removed.map((id) => labelById.get(id) ?? `#${id}`).join(', ')}</span>
                </p>
              ) : null}
              {primaryChanged ? (
                <p className="text-muted-foreground">
                  Primary owner →{' '}
                  <span className="font-medium text-foreground">
                    {primaryId ? labelById.get(primaryId) ?? `#${primaryId}` : 'nobody'}
                  </span>
                </p>
              ) : null}
            </div>
          ) : null}

          {error ? <p className="text-[11px] text-danger">{error}</p> : null}

          <div className="flex items-center justify-end gap-2 pt-1">
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="sm" disabled={isSubmitting}>
                Cancel
              </Button>
            </DialogPrimitive.Close>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                selectedIds.length === 0 ||
                // Escalating an unchanged list is still meaningful — it moves
                // the status and sends the note. Re-saving the same
                // assignments is not.
                (mode === 'assign' && !hasChanges) ||
                (mode === 'escalate' && !note.trim())
              }
            >
              {isSubmitting
                ? 'Saving…'
                : mode === 'escalate'
                  ? 'Escalate'
                  : 'Save assignments'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
